import React, { useState, useRef, useEffect } from 'react';
// ¡IMPORTANTE!: Asegúrate de instalar estas librerías en tu proyecto:
// Abre tu terminal en la raíz de tu proyecto y ejecuta:
// npm install react-avatar-editor
import AvatarEditor from 'react-avatar-editor';
// npm install browser-image-compression
import imageCompression from 'browser-image-compression';

// ¡IMPORTANTE!: Asegúrate de que el componente 'Loading.js' exista y sea accesible
// en la misma carpeta o en la ruta relativa especificada.
import Loading from './Loading'; 

// ¡IMPORTANTE!: Asegúrate de que el componente 'TextoConIcono.js' exista y sea accesible
// en la misma carpeta o en la ruta relativa especificada.
import TextoConIcono from './TextoConIcono'; 

import Avatar from '@mui/material/Avatar';
// Material UI Grid: se importa directamente desde '@mui/material/Grid'
import Grid from '@mui/material/Grid2'; 
import Button from '@mui/material/Button';
import Slider from '@mui/material/Slider';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import CreateIcon from '@mui/icons-material/Create';
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; 
import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
import ZoomInIcon from '@mui/icons-material/ZoomIn';



// La ruta a 'sin-imagen.jpg' es relativa a tu estructura de proyecto.
// Si esta imagen no se resuelve en tu entorno de desarrollo/compilación,
// puedes usar una URL de placeholder o asegurar la ruta correcta de tu archivo local.
// Por ejemplo, para un entorno de preview como este, usaremos un placeholder:
// import sinImagen from '../../assets/images/sin-imagen.jpg'; 
const sinImagen = 'https://placehold.co/100x100/A0A0A0/FFFFFF?text=Sin+Imagen'; // Placeholder para previsualización



/**
 * Componente para seleccionar, recortar y procesar una imagen.
 * Devuelve la imagen procesada como un objeto File al componente padre.
 * @param {string} imagen - URL de la imagen inicial a mostrar.
 * @param {function} onImageCropped - Función callback que se ejecuta con el File de la imagen croppeada.
 * @param {number} maxSizeMB - Tamaño máximo en MB para la compresión de la imagen.
 * @param {number} width - Ancho del área de recorte.
 * @param {number} height - Alto del área de recorte.
 * @param {boolean} avatar - Si es verdadero, muestra la imagen en un componente Avatar de Material UI.
 */
export default function ImageCropper({ imagen, onImageCropped, maxSizeMB, width, height, avatar }) {

    const usarImageCompression = true; 

    const editor = useRef(null); 

    const inputFile = useRef(null);
    const [dialogoOpen, setDialogoOpen] = useState(false);
    const [processingSpinner, setProcessingSpinner] = useState(false); 
    const [extension, setExtension] = useState("");
    const [picture, setPicture] = useState({
        img: null, 
        zoom: 2,
        rotate: 0,
        croppedImg: (imagen || sinImagen) 
    });

    const handleClickAbrirDialogo = () => {
        setDialogoOpen(true);
        setPicture(prev => ({
            ...prev,
            img: null, 
            zoom: 2,
            rotate: 0,
        }));
        inputFile.current.click();
    };

    const handleClickCerrarDialogo = () => {
        setDialogoOpen(false);
    };

    const handleSlider = (event, value) => {
        setPicture(prev => ({
            ...prev,
            zoom: value,
        }));
    };

    const handleRotate = (event, value) => {
        setPicture(prev => ({
            ...prev,
            rotate: value,
        }));
    };

    const handleSave = async () => {
        if (!editor.current) {
            console.error("Editor reference is null. Cannot save image.");
            return;
        }

        try {
            setProcessingSpinner(true); 

            const canvasScaled = editor.current.getImageScaledToCanvas();
            const mimeType = `image/${extension || 'png'}`; 
            const croppedImgBase64 = canvasScaled.toDataURL(mimeType);

            setPicture(prev => ({
                ...prev,
                croppedImg: croppedImgBase64,
                img: null 
            }));

            const croppedFile = await convertBase64ToFileAndProcess(
                croppedImgBase64,
                extension,
                maxSizeMB,
                width,
                height,
                mimeType
            );

            if (onImageCropped) {
                onImageCropped(croppedFile);
            }

            setDialogoOpen(false); 

        } catch (error) {
            console.error("Error al procesar o guardar la imagen croppeada:", error);
        } finally {
            setProcessingSpinner(false); 
        }
    };

    const urltoFile = (url, filename, mimeType) => {
        mimeType = mimeType || (url.match(/^data:([^;]+);/) || '')[1];
        return (fetch(url)
            .then(function (res) { return res.arrayBuffer(); })
            .then(function (buf) {
                return new File([buf], filename, { type: mimeType });
            })
        );
    };

    /**
     * Convierte una cadena Base64 a un objeto File y opcionalmente comprime la imagen.
     * @param {string} base64 - La imagen en formato Base64.
     * @param {string} ext - La extensión del archivo (ej. 'png', 'jpg').
     * @param {number} maxSizeBytes - El tamaño máximo deseado en MB para la compresión.
     * @param {number} targetWidth - El ancho objetivo para la compresión (usado para maxWidthOrHeight).
     * @param {number} targetHeight - El alto objetivo para la compresión (usado para maxWidthOrHeight).
     * @param {string} mimeType - El tipo MIME de la imagen (ej. 'image/png').
     * @returns {Promise<File>} Un objeto File de la imagen procesada.
     */
    async function convertBase64ToFileAndProcess(base64, ext, maxSizeBytes, targetWidth, targetHeight, mimeType) {
        const filename = `${Date.now()}.${ext}`; 
        let file = await urltoFile(base64, filename, mimeType);

        if (usarImageCompression) {
            const options = {
                maxSizeMB: maxSizeBytes,
                maxWidthOrHeight: (targetWidth > targetHeight ? targetWidth : targetHeight),
                useWebWorker: true,
                fileType: mimeType 
            };
            try {
                const compressedFile = await imageCompression(file, options);
                return compressedFile;
            } catch (error) {
                console.error("Error durante la compresión de la imagen:", error);
                throw error; 
            }
        } else {
            return file; 
        }
    }

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFile = e.target.files[0];
            const fileUrl = URL.createObjectURL(selectedFile);
            const fileNameParts = selectedFile.name.split(".");
            const fileExt = fileNameParts.length > 1 ? fileNameParts.pop() : 'png';
            setExtension(fileExt);

            setPicture(prev => ({
                ...prev,
                img: fileUrl,
                zoom: 2, 
                rotate: 0, 
            }));
            e.target.value = null; 
        }
    };

    useEffect(() => {
    }, []);

    return (
        <>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }} align="center">
                    {
                        avatar ? (
                            <Avatar sx={{ height: '75px', width: '75px' }} src={picture.croppedImg} alt="Avatar de usuario" />
                        ) : (
                            <img src={picture.croppedImg} width="100%" alt="Vista previa de la imagen" style={{ maxWidth: '100%', height: 'auto' }} />
                        )
                    }
                    <Button onClick={handleClickAbrirDialogo} size='small' startIcon={<CreateIcon />} variant="outlined">Editar</Button>
                    <input style={{ display: 'none' }} ref={inputFile} type="file" accept="image/*" onChange={handleFileChange} />
                </Grid>
            </Grid>

            <Dialog
                open={dialogoOpen}
                onClose={handleClickCerrarDialogo}
                aria-labelledby="image-cropper-dialog-title" 
                aria-describedby="image-cropper-dialog-description"
                fullWidth
                maxWidth="sm" 
            >
                <DialogTitle id="image-cropper-dialog-title">
                    {"Cargar y Recortar Imágen"}
                </DialogTitle>
                <DialogContent dividers> 
                    {
                        processingSpinner && (
                            <>
                                <Loading />
                                <p style={{ textAlign: 'center', marginTop: '20px' }}>Se está procesando la imagen, por favor espera.</p>
                            </>
                        )
                    }
                    {
                        !processingSpinner && picture.img && ( 
                            <Grid container spacing={2} sx={{ mt: 1 }}> 
                                <Grid xs={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <AvatarEditor
                                        ref={editor} 
                                        image={picture.img}
                                        width={width}
                                        height={height}
                                        border={50}
                                        color={[255, 255, 255, 0.6]} 
                                        rotate={picture.rotate}
                                        scale={picture.zoom}
                                        style={{ width: '100%', height: 'auto', display: 'block' }} 
                                    />
                                </Grid>
                                <Grid xs={12} md={6} sx={{ mt: 2, textAlign: 'center' }}> {/* Usar sx para estilos de Grid */}
                                    <TextoConIcono icono={<ZoomInIcon />} texto="Zoom" />
                                    <Slider
                                        aria-label="Zoom del Editor de Imagen"
                                        value={picture.zoom}
                                        min={1}
                                        max={10}
                                        step={0.1}
                                        onChange={handleSlider}
                                        valueLabelDisplay="auto" 
                                    />
                                </Grid>
                                <Grid xs={12} md={6} sx={{ mt: 2, textAlign: 'center' }}> {/* Usar sx para estilos de Grid */}
                                    <TextoConIcono icono={<Rotate90DegreesCwIcon />} texto="Rotación" />
                                    <Slider
                                        aria-label="Rotación del Editor de Imagen"
                                        value={picture.rotate}
                                        min={0}
                                        max={360} 
                                        step={1}
                                        onChange={handleRotate}
                                        valueLabelDisplay="auto"
                                    />
                                </Grid>
                            </Grid>
                        )
                    }
                    {
                       !processingSpinner && !picture.img && (
                           <p style={{ textAlign: 'center', color: 'text.secondary' }}>Por favor, selecciona una imagen para editar.</p>
                       )
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClickCerrarDialogo}>Cancelar</Button>
                    <Button onClick={handleSave} autoFocus variant="contained" startIcon={<CloudUploadIcon />}> 
                        Guardar Imagen
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
